var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import { useMemo } from "react";
import moment from "moment";
import { formatUnits } from "@ethersproject/units";
import { getAssets, VaultFees, VaultList, VaultVersionList, } from "../constants/constants";
import useVaultPriceHistory, { useVaultsPriceHistory, } from "./useVaultPerformanceUpdate";
import { getAssetDecimals } from "../utils/asset";
import useLidoAPY from "./useLidoOracle";
import { useV2VaultsData } from "./web3DataContext";
import { apyFromPricePerShare } from "../utils/math";
import { useSTETHStakingApr } from "../hooks/useSTETHStakingApr";
/**
 *
 * @param priceHistory
 * @param periodStart
 * @returns
 */
var getPriceHistoryFromPeriod = function (priceHistory, periodStart) {
    /**
     * Does not procees if price history length is lesser than or equal to 2
     */
    if (priceHistory.length <= 2) {
        /**
         * If only 2 points, return them
         */
        if (priceHistory.length === 2) {
            return [priceHistory[0], priceHistory[1]];
        }
        return undefined;
    }
    // add buffers of 5 hours
    // to catch any early or late settle events
    var _a = [
        periodStart.clone().subtract(5, "hours").unix(),
        periodStart.clone().add(1, "week").add(5, "hours").unix(),
        periodStart.clone().add(1, "week").subtract(5, "hours").unix(),
        periodStart.clone().add(2, "week").add(5, "hours").unix(),
    ], periodStartTimestamp = _a[0], periodEndTimestamp = _a[1], nextPeriodStartTimestamp = _a[2], nextPeriodEndTimestamp = _a[3];
    var priceHistoryInPeriod = priceHistory.filter(function (historyItem) {
        return historyItem.timestamp >= periodStartTimestamp &&
            historyItem.timestamp < periodEndTimestamp;
    });
    var priceHistoryInNextPeriod = priceHistory.filter(function (historyItem) {
        return historyItem.timestamp >= nextPeriodStartTimestamp &&
            historyItem.timestamp < nextPeriodEndTimestamp;
    });
    /**
     * Check if one of the period is empty
     */
    if (!priceHistoryInPeriod.length || !priceHistoryInNextPeriod.length) {
        /**
         * If combined length is more than or equals to 2, we simply return the first and second
         */
        if (priceHistoryInPeriod.length + priceHistoryInNextPeriod.length >= 2) {
            var combinedPriceHistory = __spreadArray(__spreadArray([], priceHistoryInPeriod), priceHistoryInNextPeriod);
            return [combinedPriceHistory[0], combinedPriceHistory[1]];
        }
        /**
         * Else, return undefined
         */
        return undefined;
    }
    return [priceHistoryInPeriod[0], priceHistoryInNextPeriod[0]];
};
/**
 * To calculate APY, we first find a week that has not been exercised.
 * This is to remove accounting of losses into calculating APY.
 * The calculation shall be calculated based on the start of previous period versus the the start of current period.
 * Each period consist of a week, starting from a friday UTC 10am until the next friday UTC 10am.
 */
export var calculateAPYFromPriceHistory = function (priceHistory, decimals, _a, underlyingYieldAPR) {
    var _b, _c;
    var vaultOption = _a.vaultOption, vaultVersion = _a.vaultVersion;
    var newPriceHistory = priceHistory;
    // Ignore apy calculation for specific avax vaults
    // Ignore priceHistory before round 16
    var ignoreRoundsBefore = undefined;
    if (vaultOption === "rUSDC-AVAX-P-THETA") {
        ignoreRoundsBefore = 16;
    }
    else if (vaultOption === "rAVAX-THETA") {
        ignoreRoundsBefore = 30;
    }
    else if (vaultOption === "rsAVAX-THETA") {
        ignoreRoundsBefore = 18;
    }
    // Fix for avax apys
    if (ignoreRoundsBefore) {
        newPriceHistory = priceHistory.filter(function (h) {
            return (h.round || 0) >= ignoreRoundsBefore;
        });
    }
    var periodStart = moment()
        .isoWeekday("friday")
        .utc()
        .set("hour", 10)
        .set("minute", 0)
        .set("second", 0)
        .set("millisecond", 0);
    if (periodStart.isAfter(moment())) {
        periodStart.subtract(1, "week");
    }
    var weeksAgo = 0;
    var apys = [];
    while (true) {
        var priceHistoryFromPeriod = getPriceHistoryFromPeriod(newPriceHistory, periodStart);
        if (priceHistoryFromPeriod) {
            var _d = [
                parseFloat(formatUnits(priceHistoryFromPeriod[0].pricePerShare, decimals)),
                parseFloat(formatUnits(priceHistoryFromPeriod[1].pricePerShare, decimals)),
            ], startingPricePerShare = _d[0], endingPricePerShare = _d[1];
            // Only includes weeks that is positive
            if (endingPricePerShare > startingPricePerShare) {
                if (weeksAgo > 0) {
                    /**
                     * Fees and underlying yields had been accounted
                     */
                    apys.push(apyFromPricePerShare(startingPricePerShare, endingPricePerShare) +
                        underlyingYieldAPR);
                }
                else {
                    switch (vaultVersion) {
                        case "v1":
                            /**
                             * V1 does not have fees that can impact performance
                             */
                            apys.push(apyFromPricePerShare(startingPricePerShare, endingPricePerShare) + underlyingYieldAPR);
                            break;
                        case "earn":
                        case "v2":
                            /**
                             * We first calculate price per share after annualized management fees are charged
                             */
                            var endingPricePerShareAfterManagementFees = endingPricePerShare *
                                (1 -
                                    parseFloat((_b = VaultFees[vaultOption].v2) === null || _b === void 0 ? void 0 : _b.managementFee) /
                                        100 /
                                        52);
                            /**
                             * Next, we calculate how much performance fees will lower the pricePerShare
                             */
                            var performanceFeesImpact = (endingPricePerShare - startingPricePerShare) *
                                (parseFloat((_c = VaultFees[vaultOption].v2) === null || _c === void 0 ? void 0 : _c.performanceFee) / 100);
                            /**
                             * Finally, we calculate price per share after both fees
                             */
                            var pricePerShareAfterFees = endingPricePerShareAfterManagementFees - performanceFeesImpact;
                            apys.push(apyFromPricePerShare(startingPricePerShare, pricePerShareAfterFees) + underlyingYieldAPR);
                            break;
                    }
                }
            }
        }
        // Continue to previous week
        periodStart.subtract(1, "week");
        weeksAgo += 1;
        /**
         * Prevent searching too far ago
         */
        if (weeksAgo >= 4) {
            break;
        }
    }
    // Calculate average apy
    return apys.reduce(function (acc, curr) { return acc + curr; }, 0) / apys.length || 0;
};
export var useLatestAPY = function (vaultOption, vaultVersion) {
    var _a = useVaultPriceHistory(vaultOption, vaultVersion), priceHistory = _a.priceHistory, vaultPriceHistoryLoading = _a.loading;
    var loading = vaultPriceHistoryLoading;
    var currentApr = useSTETHStakingApr().currentApr;
    var underlyingYieldAPR = 0;
    switch (vaultOption) {
        case "rstETH-THETA":
            underlyingYieldAPR = currentApr;
            break;
    }
    return {
        fetched: !loading,
        res: loading
            ? 0
            : calculateAPYFromPriceHistory(priceHistory, getAssetDecimals(getAssets(vaultOption)), { vaultOption: vaultOption, vaultVersion: vaultVersion }, underlyingYieldAPR),
    };
};
export default useLatestAPY;
export var useLatestAPYs = function () {
    var _a = useVaultsPriceHistory(), vaultsPriceHistory = _a.data, vaultsPriceHistoryLoading = _a.loading;
    var _b = useV2VaultsData(), vaultsData = _b.data, vaultsDataLoading = _b.loading;
    var loading = vaultsPriceHistoryLoading || vaultsDataLoading;
    var lidoAPY = useLidoAPY();
    var latestAPYs = useMemo(function () {
        return Object.fromEntries(VaultVersionList.map(function (version) { return [
            version,
            Object.fromEntries(VaultList.map(function (vaultOption) {
                var priceHistory = vaultsPriceHistory[version][vaultOption];
                var _a = vaultsData[vaultOption], round = _a.round, pricePerShare = _a.pricePerShare;
                switch (version) {
                    case "earn":
                    case "v2":
                        switch (vaultOption) {
                        }
                }
                var underlyingYieldAPR = 0;
                switch (vaultOption) {
                    case "rstETH-THETA":
                        underlyingYieldAPR = lidoAPY * 100;
                }
                return [
                    vaultOption,
                    loading
                        ? 0
                        : calculateAPYFromPriceHistory(priceHistory.map(function (history) {
                            return history.round === round
                                ? __assign(__assign({}, history), { pricePerShare: pricePerShare }) : history;
                        }), getAssetDecimals(getAssets(vaultOption)), { vaultOption: vaultOption, vaultVersion: version }, underlyingYieldAPR),
                ];
            })),
        ]; }));
    }, [vaultsData, vaultsPriceHistory, lidoAPY, loading]);
    return { fetched: !loading, res: latestAPYs };
};
//# sourceMappingURL=useLatestAPY.js.map